import React, {useMemo, useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import Badge from '../../../Presentation/components/ui/Badge';
import Spinner from '../../../Presentation/components/ui/Spinner';
import {AppText, AppCardTitle, AppSmallText, AppBodyText, AppLabelText, AppSectionTitle, AppTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import Modal from '../../../Presentation/components/ui/Modal';
import TableToolbar from '../../../Presentation/components/ui/TableToolbar';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';
import {formatDate} from '../../../Presentation/utils/dateUtils';
import {formatPaymentTerms} from '../../../Presentation/utils/paymentTermsUtils';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';
import {
  Eye,
  Receipt,
  Download,
  User,
  Store,
  CreditCard,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Clock,
  DollarSign,
  FileText,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import type {Sale, SaleItem} from '../models/sale.model';
import {useSalesUIStore} from '../store/salesUIStore';
import {useSalesDashboardStore} from '../store/salesDashboardStore';
import InvoiceAttachmentCard from './InvoiceAttachmentCard';

interface SalesTableProps {
  sales: Sale[];
  loading?: boolean;
  paginatedData?: {
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  };
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewDetails?: (sale: Sale) => void;

  // Search and Filter props
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onRefresh?: () => void;
  refreshing?: boolean;
}

const SalesTable: React.FC<SalesTableProps> = ({
  sales = [],
  loading = false,
  paginatedData,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  searchValue = '',
  onSearchChange,
  onRefresh,
  refreshing = false
}) => {
  const {hasPermission} = useAuthorization();
  const safeSales = useMemo(() => sales || [], [sales]);
  const {
    selectedSale, isDetailsModalOpen, loadingDetails,
    setSelectedSale, openDetailsModal, closeDetailsModal, setLoadingDetails
  } = useSalesUIStore();
  const clearInvoiceAttachment = useSalesDashboardStore((s) => s.clearInvoiceAttachment);
  const navigate = useNavigate();
  const [expandedSerialGroupKey, setExpandedSerialGroupKey] = useState<string | null>(null);

  const handleViewInMovements = (serial: string) => {
    navigate(`/dashboard/inventory?tab=movements&serial=${encodeURIComponent(serial)}`);
  };

  // Reset expanded serials when sale changes
  useEffect(() => {
    setExpandedSerialGroupKey(null);
  }, [selectedSale?.id]);

  // Group line items by product (productId:unitPrice:productName) to avoid repeated rows
  const groupedItems = useMemo(() => {
    const items = selectedSale?.items;
    if (!items || items.length === 0) return [];
    const map = new Map<string, { productId: string; productName: string; unit: string; quantity: number; unitPrice: number; subtotal: number; serialNumbers: string[] }>();
    for (const item of items) {
      const unitPriceNorm = (item.unitPrice ?? 0).toFixed(2);
      const key = `${item.productId}:${unitPriceNorm}:${item.productName}`;
      const itemSubtotal = item.subtotal ?? (item.quantity ?? 0) * (item.unitPrice ?? 0);
      const existing = map.get(key);
      const serials = item.serialNumber ? [item.serialNumber] : [];
      if (existing) {
        existing.quantity += item.quantity ?? 0;
        existing.subtotal += itemSubtotal;
        existing.serialNumbers.push(...serials);
      } else {
        map.set(key, {
          productId: item.productId,
          productName: item.productName,
          unit: item.unit,
          quantity: item.quantity ?? 0,
          unitPrice: item.unitPrice ?? 0,
          subtotal: itemSubtotal,
          serialNumbers: serials
        });
      }
    }
    return Array.from(map.entries()).map(([key, g], idx) => ({ groupKey: key, index: idx + 1, ...g }));
  }, [selectedSale?.items]);

  const handlePageChange = (page: number) => {
    onPageChange?.(page);
  };

  const handlePageSizeChange = (pageSize: number) => {
    onPageSizeChange?.(pageSize);
  };

  const handleViewDetails = async (sale: Sale) => {
    setLoadingDetails(true);
    openDetailsModal(sale);

    try {
      // Call the parent's onViewDetails to fetch detailed data
      const detailedSale = await onViewDetails?.(sale);

      if (detailedSale) {
        setSelectedSale(detailedSale);
      } else {
        // Fallback to basic sale data if detailed fetch fails
        setSelectedSale(sale);
      }
    } catch (error) {
      // Fallback to basic sale data
      setSelectedSale(sale);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetailsModal = () => {
    clearInvoiceAttachment();
    closeDetailsModal();
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { text: string; variant: 'success' | 'warning' | 'destructive' }> = {
      delivered: { text: 'تم التسليم', variant: 'success' },
      pending: { text: 'معلقة', variant: 'warning' },
      cancelled: { text: 'ملغية', variant: 'destructive' }
    };
    const config = map[status] ?? { text: 'معلقة', variant: 'warning' as const };
    return <Badge variant={config.variant} size="sm">{config.text}</Badge>;
  };

  const getPaymentStatusBadge = (paymentMethod: string) => {
    const map: Record<string, { text: string; variant: 'success' | 'default' | 'secondary' }> = {
      cash: { text: 'نقداً', variant: 'success' },
      credit: { text: 'آجل', variant: 'default' },
      bank_transfer: { text: 'تحويل بنكي', variant: 'secondary' }
    };
    const config = map[paymentMethod] ?? { text: 'آجل', variant: 'default' as const };
    return <Badge variant={config.variant} size="sm">{config.text}</Badge>;
  };


  const columns: TableColumn<Sale>[] = [
    {
      key: 'invoiceNumber',
      title: 'رقم الفاتورة',
      render: (sale) => {
        const isPaymentInvoice = sale.invoiceNumber.startsWith('PAY-');
        const isRegularInvoice = sale.invoiceNumber.startsWith('INV-');

        return (
          <div className="flex flex-col">
            <AppText className={`font-medium ${isPaymentInvoice ? 'text-primary' : isRegularInvoice ? 'text-success' : 'text-gray-900'}`}>
              {sale.invoiceNumber}
            </AppText>
            <AppSmallText className="text-gray-500">
              {isPaymentInvoice ? 'فاتورة دفع' : isRegularInvoice ? 'فاتورة مبيعات' : 'فاتورة'}
            </AppSmallText>
          </div>
        );
      }
    },
    {
      key: 'marketer_name',
      title: 'اسم المسوق',
      render: (sale) => (
        <AppText className="text-gray-600">
          {sale.marketerName}
        </AppText>
      )
    },
    {
      key: 'store_name',
      title: 'اسم المتجر',
      render: (sale) => (
        <div className="flex flex-col">
          <AppText className="font-medium text-gray-900">
            {sale.storeName}
          </AppText>
        </div>
      )
    },
    {
      key: 'total_amount',
      title: 'قيمة الفاتورة',
      render: (sale) => (
        <AppText className="font-medium text-gray-900">
          {formatCurrency(sale.totalAmount)}
        </AppText>
      )
    },
    {
      key: 'sale_date',
      title: 'تاريخ الفاتورة',
      render: (sale) => (
        <DateTimeDisplay dateString={sale.saleDate} />
      )
    },
    {
      key: 'due_date',
      title: 'تاريخ الاستحقاق',
      render: (sale) => (
        <DateTimeDisplay dateString={sale.dueDate} />
      )
    },
    {
      key: 'status',
      title: 'حالة الفاتورة',
      render: (sale) => getStatusBadge(sale.status)
    }
  ];

  const actions = (sale: Sale) => {
    const actions = [];

    // View Details - Requires sales read permission
    if (hasPermission(PERMISSIONS.READ_SALES) || hasPermission(PERMISSIONS.MANAGE_SALES)) {
      actions.push({
        label: 'عرض التفاصيل',
        icon: <Eye className="text-primary" size={20} />,
        onClick: () => handleViewDetails(sale)
      });
    }

    // Download Invoice - Requires sales read permission
    if (hasPermission(PERMISSIONS.READ_SALES) || hasPermission(PERMISSIONS.MANAGE_SALES)) {
      actions.push({
        label: 'تحميل الفاتورة',
        icon: <Download className="text-success" size={20} />,
        onClick: () => {
          // Download invoice functionality - placeholder implementation
          // Invoice download functionality not yet implemented
        }
      });
    }

    return actions;
  };

  // Export configuration using new Excel export system
  const {createConfig} = useExportConfig<Sale>();

  const exportConfig = createConfig([
    {key: 'invoiceNumber', title: 'رقم الفاتورة'},
    {key: 'marketerName', title: 'اسم المسوق'},
    {key: 'storeName', title: 'اسم المتجر'},
    {key: 'totalAmount', title: 'قيمة الفاتورة', formatter: ExportFormatters.currencySARExport},
    {key: 'saleDate', title: 'تاريخ الفاتورة', formatter: ExportFormatters.dateTime},
    {key: 'dueDate', title: 'تاريخ الاستحقاق', formatter: ExportFormatters.dateTime},
    {
      key: 'status',
      title: 'حالة الفاتورة',
      formatter: (value) => {
        switch(value) {
          case 'pending': return 'في الانتظار';
          case 'approved': return 'معتمدة';
          case 'completed': return 'مكتملة';
          case 'cancelled': return 'ملغية';
          default: return String(value || 'غير محدد');
        }
      }
    },
    {
      key: 'paymentMethod',
      title: 'طريقة الدفع',
      formatter: (value) => {
        switch(value) {
          case 'cash': return 'نقداً';
          case 'credit': return 'آجل';
          case 'both': return 'نقداً + آجل';
          default: return String(value || 'غير محدد');
        }
      }
    },
    {key: 'cashPaid', title: 'المبلغ المدفوع', formatter: ExportFormatters.currencySARExport},
    {key: 'remainingDebt', title: 'المبلغ المتبقي', formatter: ExportFormatters.currencySARExport},
    {
      key: 'paymentStatus',
      title: 'حالة الدفع',
      formatter: (value) => {
        switch(value) {
          case 'paid': return 'مدفوعة';
          case 'pending': return 'في الانتظار';
          case 'approved': return 'معتمدة';
          default: return String(value || 'غير محدد');
        }
      }
    },
    {key: 'collectionStage', title: 'مرحلة التحصيل'},
    {
      key: 'daysOverdue',
      title: 'أيام التأخير',
      formatter: (value) => {
        const days = Number(value) || 0;
        return days > 0 ? `${days} يوم` : 'لا يوجد تأخير';
      }
    }
  ], 'فواتير_المبيعات', 'تصدير فواتير المبيعات', 'تصدير جميع فواتير المبيعات مع التفاصيل المالية');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: safeSales,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  if (loading) {
    return (
      <div className="w-full">
        <Card className="p-6">
          <div className="flex items-center justify-center gap-3 py-8">
            <Spinner size={32} />
            <AppText className="text-gray-600">جاري تحميل الفواتير...</AppText>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Card className="overflow-visible">
        {/* Header */}
        <div className="p-6 bg-gray-50 border-b">
          <div className="flex justify-between items-center">
            <div>
              <AppSectionTitle className="font-bold text-gray-900">
                قائمة الفواتير ({paginatedData?.pagination?.total || safeSales.length}) إدارة وعرض جميع فواتير المبيعات
              </AppSectionTitle>
            </div>

            <div className="flex items-center gap-4">
              {/* Export Button - New Excel Export System */}
              <ExportButton
                onExport={handleExport}
                isExporting={isExporting}
                canExport={canExport}
                recordCount={recordCount}
                showDropdown={true}
              />
            </div>
          </div>
        </div>

        {/* Toolbar with Search */}
        <TableToolbar
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder="البحث في الفواتير..."
          onRefresh={onRefresh}
          refreshing={refreshing}
          exportable={false}
        />

        {/* Table with horizontal scroll */}
        <div className="overflow-x-auto">
          <AppTable
            columns={columns}
            data={safeSales}
            rowKey={(record, index) => `sale-${record.id}-${index || 0}`}
            emptyText="لا توجد فواتير مسجلة"
            page={paginatedData?.pagination?.page}
            pageSize={paginatedData?.pagination?.limit}
            totalCount={paginatedData?.pagination?.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            showPaginationInfo={true}
            loading={loading}
            loadingRows={paginatedData?.pagination?.limit || 10}
            loadingMessage="جاري تحميل المبيعات..."
            actions={actions}
          />
        </div>
      </Card>

      {/* Details Modal */}
      <Modal
        open={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        title="تفاصيل الفاتورة"
        width="max-w-6xl"
      >
        {loadingDetails ? (
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <Spinner size={48} />
            <AppText className="text-gray-600">جاري تحميل تفاصيل الفاتورة...</AppText>
          </div>
        ) : selectedSale ? (
          <div className="space-y-6">
            {/* Header Section */}
            <div className="bg-primary/5 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <Receipt className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <AppCardTitle className="font-bold text-gray-900">
                      {selectedSale.invoiceNumber}
                    </AppCardTitle>
                    <AppBodyText className="text-gray-600">
                      فاتورة البيع
                    </AppBodyText>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(selectedSale.status)}
                    {getPaymentStatusBadge(selectedSale.paymentMethod)}
                  </div>
                  <AppBodyText className="text-gray-500">
                    {formatDate(selectedSale.saleDate)}
                  </AppBodyText>
                </div>
              </div>
            </div>

            {/* Financial Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-4 border-l-4 border-l-primary">
                <div className="flex items-center justify-between">
                  <div>
                    <AppLabelText className="font-medium text-gray-600">المبلغ الإجمالي</AppLabelText>
                    <AppTitle className="font-bold text-primary">
                      {formatCurrency(selectedSale.totalAmount)}
                    </AppTitle>
                  </div>
                  <DollarSign className="h-8 w-8 text-primary" />
                </div>
              </Card>

              <Card className="p-4 border-l-4 border-l-success">
                <div className="flex items-center justify-between">
                  <div>
                    <AppLabelText className="font-medium text-gray-600">المبلغ المدفوع</AppLabelText>
                    <AppTitle className="font-bold text-success">
                      {formatCurrency(selectedSale.cashPaid)}
                    </AppTitle>
                  </div>
                  <CheckCircle className="h-8 w-8 text-success" />
                </div>
              </Card>

              <Card className={`p-4 border-l-4 ${selectedSale.remainingDebt > 0 ? 'border-l-error' : 'border-l-success'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <AppLabelText className="font-medium text-gray-600">المبلغ المتبقي</AppLabelText>
                    <AppTitle className={`font-bold ${selectedSale.remainingDebt > 0 ? 'text-error' : 'text-success'}`}>
                      {formatCurrency(selectedSale.remainingDebt)}
                    </AppTitle>
                  </div>
                  {selectedSale.remainingDebt > 0 ? (
                    <AlertCircle className="h-8 w-8 text-error" />
                  ) : (
                    <CheckCircle className="h-8 w-8 text-success" />
                  )}
                </div>
              </Card>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Marketer Information */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <User className="h-5 w-5 text-purple-600" />
                  </div>
                  <AppSectionTitle className="font-semibold text-gray-900">معلومات المسوق</AppSectionTitle>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">الاسم الكامل</AppLabelText>
                      <AppText className="font-medium">{selectedSale.marketerName}</AppText>
                    </div>
                  </div>
                  {selectedSale.marketerUsername && (
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">اسم المستخدم</AppLabelText>
                        <AppText className="font-medium">{selectedSale.marketerUsername}</AppText>
                      </div>
                    </div>
                  )}
                  {selectedSale.marketerEmail && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">البريد الإلكتروني</AppLabelText>
                        <AppText className="font-medium">{selectedSale.marketerEmail}</AppText>
                      </div>
                    </div>
                  )}
                  {selectedSale.marketerPhone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">رقم الهاتف</AppLabelText>
                        <AppText className="font-medium">{selectedSale.marketerPhone}</AppText>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Store Information */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Store className="h-5 w-5 text-green-600" />
                  </div>
                  <AppSectionTitle className="font-semibold text-gray-900">معلومات المتجر</AppSectionTitle>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Store className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">اسم المتجر</AppLabelText>
                      <AppText className="font-medium">{selectedSale.storeName}</AppText>
                    </div>
                  </div>
                  {selectedSale.storeManagerName && (
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">مدير المتجر</AppLabelText>
                        <AppText className="font-medium">{selectedSale.storeManagerName}</AppText>
                      </div>
                    </div>
                  )}
                  {selectedSale.storePhone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">رقم الهاتف</AppLabelText>
                        <AppText className="font-medium">{selectedSale.storePhone}</AppText>
                      </div>
                    </div>
                  )}
                  {(selectedSale.storeCity || selectedSale.storeRegion) && (
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">الموقع</AppLabelText>
                        <AppText className="font-medium">
                          {[selectedSale.storeCity, selectedSale.storeRegion].filter(Boolean).join(', ')}
                        </AppText>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Payment & Timeline Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Information */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-yellow-600" />
                  </div>
                  <AppSectionTitle className="font-semibold text-gray-900">معلومات الدفع</AppSectionTitle>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">طريقة الدفع</AppLabelText>
                      {getPaymentStatusBadge(selectedSale.paymentMethod)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">شروط الدفع</AppLabelText>
                      <AppText className="font-medium">{formatPaymentTerms(selectedSale.paymentTerms)}</AppText>
                    </div>
                  </div>
                  {selectedSale.dueDate && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <div>
                        <AppLabelText className="text-gray-600">تاريخ الاستحقاق</AppLabelText>
                        <AppText className="font-medium">{formatDate(selectedSale.dueDate)}</AppText>
                      </div>
                    </div>
                  )}
                  {selectedSale.daysOverdue > 0 && (
                    <div className="flex items-center gap-3">
                      <AlertCircle className="h-4 w-4 text-error" />
                      <div>
                        <AppLabelText className="text-gray-600">أيام التأخير</AppLabelText>
                        <AppText className="font-medium text-error">{selectedSale.daysOverdue} يوم</AppText>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Timeline Information */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Clock className="h-5 w-5 text-indigo-600" />
                  </div>
                  <AppSectionTitle className="font-semibold text-gray-900">معلومات التوقيت</AppSectionTitle>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">تاريخ البيع</AppLabelText>
                      <DateTimeDisplay dateString={selectedSale.saleDate} dateClassName="font-medium" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">تاريخ الإنشاء</AppLabelText>
                      <DateTimeDisplay dateString={selectedSale.createdAt} dateClassName="font-medium" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <AppLabelText className="text-gray-600">آخر تحديث</AppLabelText>
                      <DateTimeDisplay dateString={selectedSale.updatedAt} dateClassName="font-medium" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Items Section */}
            {groupedItems.length > 0 ? (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-warning/10 rounded-lg">
                    <FileText className="h-5 w-5 text-warning" />
                  </div>
                  <AppCardTitle className="text-gray-900">عناصر البيع</AppCardTitle>
                  <Badge variant="warning" size="sm">{groupedItems.length} عنصر</Badge>
                </div>
                <div className="overflow-x-auto">
                  <AppTable<{ groupKey: string; index: number; productId: string; productName: string; unit: string; quantity: number; unitPrice: number; subtotal: number; serialNumbers: string[] }>
                    columns={[
                      {
                        key: 'product',
                        title: 'المنتج',
                        render: (g) => (
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-medium text-gray-600">{g.index}</span>
                            </div>
                            <div>
                              <AppText className="font-medium text-gray-900">{g.productName}</AppText>
                              {g.unit && (
                                <AppSmallText className="text-gray-500">الوحدة: {g.unit}</AppSmallText>
                              )}
                            </div>
                          </div>
                        )
                      },
                      {
                        key: 'serialNumber',
                        title: 'الرقم التسلسلي',
                        render: (g) => {
                          const serials = g.serialNumbers.filter(Boolean);
                          if (serials.length === 0) {
                            return <AppText className="text-gray-400">—</AppText>;
                          }
                          const isExpanded = expandedSerialGroupKey === g.groupKey;
                          const displaySerials = serials.length > 50 ? serials.slice(0, 50) : serials;
                          const hasMore = serials.length > 50;
                          return (
                            <div>
                              <button
                                type="button"
                                onClick={() => setExpandedSerialGroupKey(isExpanded ? null : g.groupKey)}
                                className="flex items-center gap-1 text-sm text-primary hover:underline"
                              >
                                {isExpanded ? `إخفاء (${serials.length})` : `عرض (${serials.length})`}
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                              {isExpanded && (
                                <div className="mt-1 max-h-40 overflow-auto rounded-lg border p-3 bg-gray-50">
                                  <div dir="ltr" className="font-mono text-xs space-y-0.5">
                                    {displaySerials.map((s) => (
                                      <div key={s}>{s}</div>
                                    ))}
                                  </div>
                                  {hasMore && <div className="text-gray-500 text-xs mt-1">والمزيد...</div>}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => handleViewInMovements(serials[0])}
                                className="mt-1 text-xs text-primary hover:underline"
                              >
                                عرض في حركة المخزون
                              </button>
                            </div>
                          );
                        }
                      },
                      {
                        key: 'quantity',
                        title: 'الكمية',
                        render: (g) => <AppText className="font-medium text-gray-900">{g.quantity}</AppText>
                      },
                      {
                        key: 'unitPrice',
                        title: 'سعر الوحدة',
                        render: (g) => <AppText className="text-gray-900">{formatCurrency(g.unitPrice)}</AppText>
                      },
                      {
                        key: 'subtotal',
                        title: 'الإجمالي',
                        render: (g) => (
                          <AppText className="font-medium text-gray-900">{formatCurrency(g.subtotal)}</AppText>
                        )
                      }
                    ]}
                    data={groupedItems}
                    rowKey={(g) => g.groupKey}
                    emptyText="لا توجد عناصر"
                    hidePagination
                    className="!mt-0"
                  />
                </div>
                <div className="mt-4 flex justify-end border-t border-gray-200 pt-4">
                  <AppText className="text-base font-bold text-gray-900">
                    الإجمالي: {formatCurrency(selectedSale.totalAmount)}
                  </AppText>
                </div>
              </Card>
            ) : (
              <Card className="p-6">
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <AppText className="text-gray-600 mb-2">لا توجد عناصر للفاتورة</AppText>
                  <AppBodyText className="text-gray-500">
                    قد تكون البيانات الأساسية فقط متاحة. تحقق من وحدة التحكم للحصول على تفاصيل أكثر.
                  </AppBodyText>
                </div>
              </Card>
            )}

            {/* Notes Section */}
            {selectedSale.notes && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <FileText className="h-5 w-5 text-gray-600" />
                  </div>
                  <AppSectionTitle className="font-semibold text-gray-900">ملاحظات</AppSectionTitle>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-l-gray-400">
                  <AppText className="text-gray-900">{selectedSale.notes}</AppText>
                </div>
              </Card>
            )}

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedSale.invoicePdfUrl && (
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <Download className="h-5 w-5 text-primary" />
                    <div>
                      <AppText className="font-medium">فاتورة PDF</AppText>
                      <AppBodyText className="text-gray-600">متاحة للتحميل</AppBodyText>
                    </div>
                  </div>
                </Card>
              )}
              {selectedSale.transferImageUrl && (
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-5 w-5 text-success" />
                    <div>
                      <AppText className="font-medium">صورة التحويل</AppText>
                      <AppBodyText className="text-gray-600">متاحة للعرض</AppBodyText>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Invoice Attachment Section */}
            <InvoiceAttachmentCard invoiceId={selectedSale.id} />
          </div>
        ) : null}
      </Modal>

    </div>
  );
};

export default SalesTable;
